<script>
import {
  authMethods,
  authFackMethods,
  notificationMethods,
} from "@/state/helpers";
import {mapState} from "vuex";

import appConfig from "@/app.config";
import {required, email} from "vuelidate/lib/validators";
import axios from "axios";

/**
 * Login component
 */
export default {
  page: {
    title: "Login",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
  },
  data() {
    return {
      email: "",
      password: "",
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  computed: {
    ...mapState("authfack", ["status"]),
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  methods: {
    ...authMethods,
    ...authFackMethods,
    ...notificationMethods,
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
          this.tryingToLogIn = true;
          // Reset the authError if it existed.
          this.authError = null;
          return (
              this.logIn({
                email: this.email,
                password: this.password,
              })
                  // eslint-disable-next-line no-unused-vars
                  .then((token) => {
                    this.tryingToLogIn = false;
                    this.isAuthError = false;
                    // Redirect to the originally requested page, or to the home page
                    this.$router.push(
                        this.$route.query.redirectFrom || {
                          name: "default",
                        }
                    );
                  })
                  .catch((error) => {
                    this.tryingToLogIn = false;
                    this.authError = error ? error : "";
                    this.isAuthError = true;
                  })
          );
        } else if (process.env.VUE_APP_DEFAULT_AUTH === "fakebackend") {
          const {email, password} = this;
          if (email && password) {
            this.login({
              email,
              password,
            }).then((res) => {
              return res;
            });
          }
        } else if (process.env.VUE_APP_DEFAULT_AUTH === "authapi") {
          this.tryingToLogIn = true;
          this.isAuthError = false;
          this.authError = null;

          const {email, password} = this;
          if (email && password) {
            axios.get('/sanctum/csrf-cookie').then(() => {
              axios.post("/api/login", {
                email: this.email,
                password: this.password,
              })
                  .then((response_login) => {
                    this.tryingToLogIn = false;
                    this.isAuthError = false;

                    const user = response_login.data.user;
                    if (user.token) {
                      // store user details and jwt token in local storage to keep user logged in between page refreshes
                      localStorage.setItem('user', JSON.stringify(user));
                    }

                    this.$router.push(
                        this.$route.query.redirectFrom || {
                          name: "default",
                        }
                    );
                  })
                  .catch((error) => {
                    this.tryingToLogIn = false;
                    this.authError = error.response ? error.response.data.error : error;
                    this.isAuthError = true;
                  });
            });
          }
        }
      }
    },
  },
  mounted() {
  },
};
</script>

<template>
  <div>
    <div class="container-fluid p-0">
      <div class="row g-0">
        <div class="col-xl-9">
          <div class="auth-full-bg pt-lg-5 p-4">
            <div class="w-100">
              <div class="bg-overlay"></div>
              <div class="d-flex h-100 flex-column">
                <div class="p-4 mt-auto">
                  <div class="row justify-content-center">
                    <div class="col-lg-7">
                      <div class="text-center">
                        <h4 class="mb-3">
                          Donation <span class="text-secondary shadow">Jacques Henri</span> Lartigue
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->

        <div class="col-xl-3">
          <div class="auth-full-page-content p-md-5 p-4">
            <div class="w-100">
              <div class="d-flex flex-column h-100">
                <div class="mb-4 mb-md-5">
                  <router-link to="/" class="d-block auth-logo">
                    <img
                        src="@/assets/images/logo-dark.png"
                        alt=""
                        height="25"
                        class="auth-logo-dark m-auto"
                    />
                    <img
                        src="@/assets/images/logo-light.png"
                        alt=""
                        height="25"
                        class="auth-logo-light m-auto"
                    />
                  </router-link>
                </div>
                <div class="my-auto">
                  <div>
                    <h5 class="text-primary">{{ $t('pages.login.hello') }}</h5>
                    <p class="text-muted">{{ $t('pages.login.hello_text') }}</p>
                  </div>
                  <b-alert
                      v-model="isAuthError"
                      variant="danger"
                      class="mt-3"
                      dismissible
                  >{{ authError }}</b-alert
                  >

                  <div
                      v-if="notification.message"
                      :class="'alert ' + notification.type"
                  >
                    {{ notification.message }}
                  </div>
                  <div class="mt-4">
                    <form  @submit.prevent="tryToLogIn">
                      <b-form-group
                          class="mb-3"
                          id="input-group-1"
                          :label="$t('pages.login.email')"
                          label-for="input-1"
                      >
                        <b-form-input
                            id="input-1"
                            v-model="email"
                            type="text"
                            :placeholder="$t('pages.login.email_placeholder')"
                            :class="{
                            'is-invalid': submitted && $v.email.$error,
                          }"
                        ></b-form-input>
                        <div
                            v-if="submitted && $v.email.$error"
                            class="invalid-feedback"
                        >
                          <span v-if="!$v.email.required"
                          >{{ $t('pages.login.errors.email_required') }}</span
                          >
                          <span v-if="!$v.email.email"
                          >{{ $t('pages.login.errors.email_invalid') }}</span
                          >
                        </div>
                      </b-form-group>
                      <b-form-group
                          class="mb-3"
                          id="input-group-2"
                          :label="$t('pages.login.password')"
                          label-for="input-2"
                      >
                        <b-form-input
                            id="input-2"
                            v-model="password"
                            type="password"
                            :placeholder="$t('pages.login.password_placeholder')"
                            :class="{
                            'is-invalid': submitted && $v.password.$error,
                          }"
                        ></b-form-input>
                        <div
                            v-if="submitted && !$v.password.required"
                            class="invalid-feedback"
                        >
                          {{ $t('pages.login.password_required') }}
                        </div>
                      </b-form-group>

                      <div class="form-check">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="remember-check"
                        />
                        <label class="form-check-label" for="remember-check">
                          {{ $t('pages.login.remember_me') }}
                        </label>
                      </div>

                      <div class="mt-3 d-grid">
                        <b-button type="submit" variant="primary"
                        >{{ $t('pages.login.login') }}</b-button
                        >
                      </div>
                    </form>
                  </div>
                </div>

                <div class="mt-4 mt-md-5 text-center">
                  <p class="mb-0">
                    {{ new Date().getFullYear() - 1 }} - {{ new Date().getFullYear() }} © AAJHL<br>Base réalisée grâce au financement de la Fondation Lartigue abritée par la Fondation de France
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- end row -->
    </div>
    <!-- end container-fluid -->
  </div>
</template>
